import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from '../../../utils/withRouter';
import { bindActionCreators, Dispatch } from 'redux';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { FormattedMessage, injectIntl } from 'react-intl';
import memoizeOne from 'memoize-one';

import { RootState } from '../../../reducers';
import { Button, EButtonType, LocationSearchInput } from '@repo/apg-ui';
import * as _actionsJourney from '../../../actions/journey1';
import * as _actionsSearch from '../../../actions/search';
import * as _actionsMap from '../../../actions/map';
import * as _actionsProduct from '../../../actions/product';
import './Step2.scss';
import * as actions from '../../../actions/index';
import { ReactComponent as IconInnovation } from '../../../assets/icons/e_i_innovation.svg';
import { useNavigate } from 'react-router';
import Map from '../../../components/MapOverviewContainer/index';
import { RADIUS_UNIT_TYPE } from '../../../components/RadiusInput';
import { PartiallyIntegratedUserDataEC } from '../../../reducers/app';

interface Props {
  actionsJourney: typeof _actionsJourney;
  actionsSearch: typeof _actionsSearch;
  actionsProduct: typeof _actionsProduct;
  actionsMap: typeof _actionsMap;
  history: any;
  intl: any;
  locales: any;
  readyToRender: boolean;
  urlHistory: Array<string>;
  userDataEC: PartiallyIntegratedUserDataEC;
  validationMessageConfig: any;
}

const Step2 = (props: Props) => {
  const navigate = useNavigate();

  useEffect(() => {
    (window as any).gApiLoaded = new Event('gApiLoaded');
    (window as any).addEventListener('gApiLoaded', () =>
      scriptLoaded(props.userDataEC.details, props.userDataEC.locations),
    );

    if (props.userDataEC.audience === null) {
      navigate({ pathname: '/journey1/1' });
    }

    return () => {
      window.removeEventListener('gApiLoaded', () =>
        scriptLoaded(props.userDataEC.details, props.userDataEC.locations),
      );
    };
  }, [props.userDataEC.details]);

  useEffect(() => {
    if (props.readyToRender) {
      scriptLoaded(props.userDataEC.details, props.userDataEC.locations);
    }
  }, [props.readyToRender]);

  const scriptLoaded = (details: { address: string; city: any; nr: any; zipCode: any }, locations: {}) => {
    const { urlHistory, readyToRender, actionsJourney } = props;
    let userAddressPresent = false;

    Object.keys(locations).forEach((key) => {
      if (key.includes(`${details.address}, ${details.nr} ${details.zipCode} ${details.city}`)) {
        userAddressPresent = true;
      }
    });

    if (
      details.address &&
      details.city &&
      !userAddressPresent &&
      readyToRender &&
      urlHistory[urlHistory.length - 2].includes('journey1/1')
    ) {
      let address: string, types: any;

      geocodeByAddress(`${details.address}, ${details.nr}, ${details.city}, CH`)
        .then((results: any[]) => {
          const filteredResults: any[] = results.filter((result) => result.types.includes('point_of_interest'));

          if (filteredResults.length) {
            address = filteredResults[0].formatted_address;
            types = filteredResults[0].types;
            return getLatLng(filteredResults[0]);
          } else {
            address = results[0].formatted_address;
            types = ['point_of_interest'];
            return getLatLng(results[0]);
          }
        })
        .then((latLng: any) => {
          address = address.slice(0, address.lastIndexOf(','));

          actionsJourney.addLocation({
            address: address,
            lngLtd: { ...latLng },
            types: types,
            context: 'address',
            radius: 1000,
            radiusUnit: RADIUS_UNIT_TYPE.KILO_METERS,
          });
        })
        .catch((error: any) => console.error('Error', error));
    }
  };

  const isDisableButton = (locations: any) => Object.keys(locations).length === 0;

  const memoizedIsDisableButton = memoizeOne(isDisableButton);

  const handleNext = () => {
    const {
      userDataEC: { locations },
    } = props;

    if (!memoizedIsDisableButton(locations)) {
      navigate({ pathname: '/journey1/3' });
    }
  };

  return (
    <div className='step2'>
      <div className='header'>
        <h4 className='title-journey'>
          <FormattedMessage
            id='ec.journey01.step02.slideHeader1'
            defaultMessage='In welchen Ortschaften möchten Sie werben?'
          />
        </h4>
      </div>

      <div className='d-flex flex-row flex-wrap mapContainer'>
        <div className='d-flex'>
          <IconInnovation />
          <p className='text-left align-self-center ms-4 me-48 fw-normal w-100 font-16'>
            <FormattedMessage
              id='ec.journey01.step02.radiusExplanationZipCode'
              defaultMessage='Bei PLZ oder Adresseingaben wird in der Karte jeweils ein Radius (1 km) angezeigt, den Sie mit der Maus verkleinern oder vergrössern können.'
            />
          </p>
        </div>

        <div className='w-100 p-relative mt-24 autoSearch'>
          {props.readyToRender && (
            <LocationSearchInput
              hideImport={true}
              restrictedOptionGroups={['region', 'canton', 'trafficcomp', 'id']}
              locations={props.userDataEC.locations}
              onHighlightLocation={(urn: string) => props.actionsSearch.highlightLocation(urn)}
              placeholder={props.intl.formatMessage({
                id: 'ec.journey01.step02.locationInputPlaceholder',
                defaultMessage: 'Klicken und Suchbegriff eingeben',
              })}
              onRemoved={(address: string) => props.actionsJourney.removeLocation(address)}
              updateMapCenter={(centerLatLng: { lat: number; lng: number }) =>
                props.actionsMap.updateMapCenter(centerLatLng)
              }
              highlightedLocation={props.userDataEC.highlightedLocation}
              getProduct={(id: string) => props.actionsProduct.fetchProductByUrn({ urn: id })}
              onSelected={(
                address: string,
                lngLtd: { lat: number; lng: number },
                types: string[],
                product?: any,
                context?: string,
                radius?: number,
                radiusUnit?: string,
              ) =>
                props.actionsJourney.addLocation({
                  address,
                  lngLtd,
                  types,
                  product,
                  context,
                  radius,
                  radiusUnit,
                })
              }
              onUpdate={(address: string, radius: number, radiusUnit: string) =>
                props.actionsJourney.updateLocation({
                  address,
                  radius,
                  radiusUnit,
                })
              }
              getSearchResults={(payload) => {
                return props.actionsSearch.getSearchResults(payload);
              }}
              triggerImportModal={() => {}}
              importButtonLabel={<FormattedMessage id='ec.search.import' defaultMessage="Produkt-ID's importieren" />}
              kilometersLabel={<FormattedMessage id='ec.search.kilometers' defaultMessage='Kilometer' />}
              metersLabel={<FormattedMessage id='ec.search.meters' defaultMessage='Meter' />}
              radiusLabel={<FormattedMessage id='ec.search.radius' defaultMessage='Radius' />}
              translations={{
                places: {
                  label: props.intl.formatMessage({
                    id: 'ec.search.places.label',
                    defaultMessage: 'Orte (Städte, Gemeinden etc.)',
                  }),
                  description: props.intl.formatMessage({
                    id: 'ec.search.places.description',
                    defaultMessage: 'Bern, Geneva, Zürich...',
                  }),
                },
                address: {
                  label: props.intl.formatMessage({
                    id: 'ec.search.address.label',
                    defaultMessage: 'Adressen',
                  }),
                  description: props.intl.formatMessage({
                    id: 'ec.search.address.description',
                    defaultMessage: 'Bsp. Giesshübelstrasse 4, 8045 Zürich',
                  }),
                },
                cantons: {
                  label: props.intl.formatMessage({
                    id: 'ec.search.cantons.label',
                    defaultMessage: 'Kantone',
                  }),
                  description: props.intl.formatMessage({
                    id: 'ec.search.cantons.description',
                    defaultMessage: 'Zürich, Bern, Luzern, Uri, Schwyz, Obwalden...',
                  }),
                },
                traffic: {
                  label: props.intl.formatMessage({
                    id: 'ec.search.traffic.label',
                    defaultMessage: 'Verkehrsbetriebe',
                  }),
                  description: props.intl.formatMessage({
                    id: 'ec.search.traffic.description',
                    defaultMessage: 'Bernmobil, tl, SBB, ZVV...',
                  }),
                },
                postal: {
                  label: props.intl.formatMessage({
                    id: 'ec.search.postal.label',
                    defaultMessage: 'PLZ',
                  }),
                  description: props.intl.formatMessage({
                    id: 'ec.search.postal.description',
                    defaultMessage: '8045, 3004, 6003',
                  }),
                },
                id: {
                  label: props.intl.formatMessage({
                    id: 'ec.search.id.label',
                    defaultMessage: 'Produkt-ID',
                  }),
                  description: props.intl.formatMessage({
                    id: 'ec.search.id.description',
                    defaultMessage: '213201, 213202. 213203...',
                  }),
                },
                region: {
                  label: props.intl.formatMessage({
                    id: 'ec.search.regions.label',
                    defaultMessage: 'Regionen',
                  }),
                  description: props.intl.formatMessage({
                    id: 'ec.search.regions.description',
                    defaultMessage: 'Zürich, Bern, Lausanne...',
                  }),
                },
              }}
            />
          )}
        </div>

        <Map
          center={props.userDataEC.mapCenter}
          locations={props.userDataEC.locations}
          updateLatLngRad={(
            lat: number,
            lng: number,
            rad: number,
            address: string,
            types: string[],
            newAddress?: string,
          ) => {
            props.actionsJourney.updateCityLocation({
              address,
              newAddress: newAddress,
              types,
              circle: { radius: rad, radiusUnit: 'm' },
              lngLtd: { lat, lng },
            });
          }}
          showingProductInfoWindow
          onHighlightLocation={(urn: string) => props.actionsSearch.highlightLocation(urn)}
          highlightedLocation={props.userDataEC.highlightedLocation}
        />
      </div>
      <div className='d-flex twoButtons mt-5'>
        <Button onClick={() => navigate({ pathname: '/journey1/1' })} buttonType={EButtonType.InvertedPillMedium}>
          <FormattedMessage id='ec.components.buttons.back' defaultMessage='Zurück' />
        </Button>

        <Button
          onClick={handleNext}
          isDisabled={memoizedIsDisableButton(props.userDataEC.locations)}
          buttonType={EButtonType.PillMedium}
          className='ms-4'
        >
          <FormattedMessage id='ec.components.buttons.next' defaultMessage='Weiter' />
        </Button>
      </div>
    </div>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
    locales: state.locales,
  };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
  return {
    actionsJourney: bindActionCreators(_actionsJourney as any, dispatch),
    actionsSearch: bindActionCreators(_actionsSearch as any, dispatch),
    actionsMap: bindActionCreators(_actionsMap as any, dispatch),
    actionsProduct: bindActionCreators(_actionsProduct as any, dispatch),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(Step2)));
